import {
  Adjustment,
  AdjustmentType,
  AddressInfo,
  CustomerInfo,
  FulfillmentLocation,
  FinancialSummary,
  OrderItem,
  OrderMetadata,
  OrderType,
  OrderV2Data,
  PartnerInfo,
  Payment,
  PaymentMethod,
  SchedulingInfo,
  SellerInfo,
  SlaInfo,
} from './order-v2.types';
import {
  AdjustmentItemDto,
  CartItemDto,
  CustomerContextDto,
  DeliveryAddressDto,
  DeliveryContextDto,
  DispatchedByDto,
  FulfillmentAddressDto,
  FulfillmentContextDto,
  OrderV2ApiItem,
  OrderV2ApiResponse,
  PaymentDto,
  SchedulingDto,
  ResuppliedByDto,
  TotalsContextDto,
  VtexAccountDto,
} from './order-v2.api.types';

export function mapOrderV2ApiToData(orderId: string, response: OrderV2ApiResponse): OrderV2Data {
  if (!response || response.length === 0) {
    throw new Error('Pedido não encontrado na resposta da API.');
  }

  const api = response.find((item) => item.orderId === orderId) ?? response[0];
  if (api.orderId !== orderId) {
    throw new Error(`Pedido ${orderId} não encontrado na resposta da API.`);
  }

  return {
    orderId: api.orderId,
    groupOrderIds: response
      .map((item) => item.orderId)
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
    orderType: mapOrderType(api.saleContext.vtexAccount),
    status: api.status ?? '',
    statusLabel: vtexStatusLabel(api.status),
    createdAt: formatIsoDateTime(api.creationDate),
    metadata: mapMetadata(api),
    modality: mapModality(api.deliveryContext, api.fulfillmentContext),
    deliveryDate: formatIsoDay(api.deliveryContext.estimate?.estimateDay),

    alerts: [],
    invoiceNumber: api.invoiceNumber ?? null,
    timeline: [],
    items: mapItems(api.cartContext.items),

    sla: mapSla(api),
    deliveryAddress: mapDeliveryAddress(api.deliveryContext, api.customerContext),
    expeditionLocation: mapExpeditionLocation(api),
    replenishmentLocation: mapReplenishmentLocation(api.fulfillmentContext?.resuppliedBy ?? null),

    financial: mapFinancial(api.totalsContext, api.cartContext.items),
    payments: mapPayments(api.transactionContext.payments),

    customer: mapCustomer(api.customerContext),
    partner: mapPartner(api),
    seller: mapSeller(api),
  };
}

function mapOrderType(vtexAccount: VtexAccountDto): OrderType {
  return vtexAccount.type === 'ThirdParty' ? '3P' : '1P';
}

function mapMetadata(api: OrderV2ApiItem): OrderMetadata {
  return {
    commercialPolicy: api.saleContext.commercialPolicy.name,
    origin: channelTypeLabel(api.saleContext.channel.type),
    operationType: api.fulfillmentContext
      ? fulfillmentModeLabel(api.fulfillmentContext.mode)
      : 'Não disponível',
    vtexAccountId: vtexAccountLabel(api.saleContext.vtexAccount),
    orderIntegrator: api.saleContext.orderIntegrator?.name ?? 'Vtex',
  };
}

function vtexAccountLabel(account: { id: string; type: string }): string {
  if (account.type === 'Main') return 'fastshopbr';
  return account.id;
}

function vtexStatusLabel(status: string | null | undefined): string {
  if (!status) return '';
  switch (status) {
    case 'order-created':                 return 'Pedido criado';
    case 'order-completed':               return 'Pedido concluído';
    case 'on-order-completed':            return 'Pedido concluído';
    case 'payment-pending':               return 'Aguardando pagamento';
    case 'waiting-for-sellers-confirmation': return 'Aguardando confirmação dos sellers';
    case 'waiting-for-authorization':     return 'Aguardando autorização';
    case 'authorize-fulfillment':         return 'Autorizar manuseio';
    case 'ready-for-handling':            return 'Pronto para manuseio';
    case 'handling':                      return 'Em manuseio';
    case 'invoice':                       return 'Faturado';
    case 'invoiced':                      return 'Faturado';
    case 'canceled':                      return 'Cancelado';
    case 'cancellation-requested':        return 'Cancelamento solicitado';
    case 'window-to-cancel':              return 'Janela de cancelamento';
    case 'request-cancel':                return 'Solicitar cancelamento';
    case 'payment-approved':              return 'Pagamento aprovado';
    case 'approve-payment':               return 'Aprovar pagamento';
    case 'payment-denied':                return 'Pagamento negado';
    default:
      return status.replace(/-/g, ' ');
  }
}

function channelTypeLabel(type: string | null | undefined): string {
  switch (type) {
    case 'B2CSite':
      return 'Site B2C';
    case 'SellerApp':
      return 'App do vendedor';
    case 'B2BSite':
      return 'Site B2B';
    case 'IncentiveSite':
      return 'Site de incentivo';
    case 'CustomerApp':
      return 'App do cliente';
    case 'PaymentLink':
      return 'Link de pagamento';
    case 'AffiliateSite':
      return 'Site afiliado';
    default:
      return type ?? '';
  }
}

function fulfillmentModeLabel(mode: string | null): string {
  switch (mode) {
    case 'CrossDocking':
      return 'Cross-docking';
    case 'OperatorLogistic':
      return 'Operador logístico';
    case null:
      return 'Entrega direta';
    default:
      return mode;
  }
}

function mapModality(delivery: DeliveryContextDto, fulfillment: FulfillmentContextDto | null): string {
  if (delivery.type === 'Pickup') return 'Retirada em loja';
  if (!fulfillment) return 'Entrega';
  return `Entrega · ${fulfillmentModeLabel(fulfillment.mode)}`;
}

function formatIsoDay(isoDay?: string | null): string {
  if (!isoDay) return '';
  const parts = isoDay.split('-');
  if (parts.length !== 3) return isoDay;
  const [y, m, d] = parts;
  return `${d}/${m}/${y}`;
}

function formatIsoDateTime(iso?: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return (
    `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ` +
    `${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

function mapItems(items: CartItemDto[]): OrderItem[] {
  return items
    .filter((item) => item.type === 'Sku')
    .map((item) => ({
      productId: item.identifiers.productId,
      refId: item.identifiers.refId,
      name: item.name,
      quantity: item.quantity,
      price: item.pricing.basePrice.totalAmount.cents,
      sellingPrice: item.pricing.finalPrice.totalAmount.cents,
      adjustments: mapAdjustments(item.pricing.adjustments.items),
    }));
}

function mapAdjustments(items: AdjustmentItemDto[]): Adjustment[] {
  return items.map((adj) => ({
    type: normalizeAdjustmentType(adj.type),
    name: adj.name,
    description: adj.description,
    value: adj.totalAmount.cents,
  }));
}

function normalizeAdjustmentType(type: string): AdjustmentType {
  if (type === 'Coupon' || type === 'ManualPrice' || type === 'Generic') return type;
  return 'Generic';
}

function mapSla(api: OrderV2ApiItem): SlaInfo {
  return {
    deliveryDate: formatIsoDay(api.deliveryContext.estimate?.estimateDate),
    deliveryType: api.deliveryContext.type === 'Pickup' ? 'Retirada' : 'Delivery',
    businessDaysToDeliver: api.deliveryContext.estimate?.estimate ?? null,
    scheduling: mapScheduling(api.deliveryContext.estimate?.scheduling),
  };
}

function mapScheduling(scheduling: SchedulingDto | null | undefined): SchedulingInfo | null {
  if (!scheduling) return null;
  if (!scheduling.window) return null;
  if (scheduling.type === 'Normal') return null;

  return {
    typeLabel: schedulingTypeLabel(scheduling.type),
    windowStart: formatIsoDateTime(scheduling.window.start),
    windowEnd: formatIsoDateTime(scheduling.window.end),
  };
}

function schedulingTypeLabel(type: string): string {
  switch (type) {
    case 'Scheduled':
      return 'Agendado';
    case 'Programmed':
      return 'Programado';
    default:
      return type;
  }
}

function mapDeliveryAddress(
  delivery: DeliveryContextDto,
  customer: CustomerContextDto
): AddressInfo {
  return {
    recipientName: delivery.receiverName?.trim() || customer.fullName,
    fullAddress: composeDeliveryAddress(delivery.address),
    zipCode: delivery.address.postalCode,
    pickupPointId: delivery.pickup?.pickupPointId ?? null,
    pickupStoreName: delivery.pickup?.pickupStoreName ?? null,
  };
}

function composeDeliveryAddress(addr: DeliveryAddressDto): string {
  const numberAndComplement = [addr.number, addr.complement].filter(Boolean).join(', ');
  const line1 = [addr.street, numberAndComplement].filter(Boolean).join(', ');
  return [line1, addr.neighborhood, addr.city, addr.state]
    .filter((part) => part && part.trim().length > 0)
    .join(' — ');
}

function mapExpeditionLocation(api: OrderV2ApiItem): FulfillmentLocation | null {
  if (!api.fulfillmentContext) return null;

  const dispatchedBy = api.fulfillmentContext.dispatchedBy;
  const warehouse = dispatchedBy.warehouse;
  const mode = api.fulfillmentContext.mode;
  return {
    role: 'expedition',
    type: dispatchedBy.type,
    vtexAccountId: dispatchedBy.accountVtex.id,
    name: dispatchedBy.name,
    address: dispatchedBy.address ? composeFulfillmentAddress(dispatchedBy.address) : null,
    stockName: warehouse?.name ?? warehouse?.id ?? '',
    operationName: mode === null ? null : fulfillmentModeLabel(mode),
    shippingPolicy: api.fulfillmentContext.courierId ?? api.fulfillmentContext.vtexDockId,
    warehouseId: warehouse?.id,
    dock: dispatchedBy.dock ? { id: dispatchedBy.dock.id } : undefined,
    courier: dispatchedBy.courier ? { id: dispatchedBy.courier.id } : null,
  };
}

function mapReplenishmentLocation(resuppliedBy: ResuppliedByDto | null): FulfillmentLocation | null {
  if (!resuppliedBy) return null;
  return {
    role: 'replenishment',
    type: resuppliedBy.type,
    vtexAccountId: resuppliedBy.accountVtex.id,
    name: resuppliedBy.name,
    address: resuppliedBy.address ? composeFulfillmentAddress(resuppliedBy.address) : null,
  };
}

function composeFulfillmentAddress(addr: FulfillmentAddressDto): string {
  const parts = [addr.street, addr.neighborhood, addr.city, addr.state];
  const base = parts.filter((p) => p && p.trim().length > 0).join(' — ');
  return addr.zipCode ? `${base} — CEP ${addr.zipCode}` : base;
}

function mapFinancial(totals: TotalsContextDto, items: CartItemDto[]): FinancialSummary {
  const adjustmentsCount = items
    .filter((item) => item.type === 'Sku')
    .reduce((sum, item) => sum + (item.pricing.adjustments.items?.length ?? 0), 0);

  return {
    itemsSubtotal: totals.totalWithoutAdjustments.cents,
    shipping: totals.totalShipping.cents,
    totalAdjustments: totals.totalAdjustments.cents,
    adjustmentsCount,
    total: totals.totalWithAdjustments.cents,
    totalInterest: totals.totalInterest.cents,
    grandTotal: totals.total.cents,
  };
}

function mapPayments(payments: PaymentDto[]): Payment[] {
  return payments.map((p) => ({
    method: resolvePaymentMethod(p.name),
    methodLabel: p.name,
    value: p.paymentValue.cents,
    paidAt: '',
  }));
}

function resolvePaymentMethod(name: string): PaymentMethod {
  const lower = (name ?? '').toLowerCase();
  if (lower.includes('pix')) return 'pix';
  if (lower.includes('boleto')) return 'boleto';
  if (lower.includes('cart') || lower.includes('card') || lower.includes('cred'))
    return 'credit_card';
  return 'other';
}

function mapCustomer(customer: CustomerContextDto): CustomerInfo {
  if (customer.type === 'Corporate') {
    const cnpjDigits = (customer.corporateDocument ?? '').replace(/\D/g, '');
    return {
      type: 'Corporate',
      name: customer.fullName,
      documentType: 'CNPJ',
      documentNumber: formatDocument(cnpjDigits),
      email: customer.email,
      phone: formatPhone(customer.phone),
      corporateName: customer.corporateName ?? undefined,
      tradeName: customer.tradeName ?? undefined,
    };
  }

  const cpfDigits = (customer.document ?? '').replace(/\D/g, '');
  return {
    type: 'Personal',
    name: customer.fullName,
    documentType: 'CPF',
    documentNumber: formatDocument(cpfDigits),
    email: customer.email,
    phone: formatPhone(customer.phone),
  };
}

function formatPhone(raw?: string | null): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const hasCountry = digits.length > 11 && digits.startsWith('55');
  const country = hasCountry ? '+55 ' : '';
  const local = hasCountry ? digits.slice(2) : digits;
  if (local.length === 11) {
    return `${country}(${local.slice(0, 2)}) ${local.slice(2, 7)}-${local.slice(7)}`;
  }
  if (local.length === 10) {
    return `${country}(${local.slice(0, 2)}) ${local.slice(2, 6)}-${local.slice(6)}`;
  }
  return raw;
}

function formatDocument(digits: string): string {
  if (digits.length === 11) {
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
  }
  if (digits.length === 14) {
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
  }
  return digits;
}

function mapPartner(api: OrderV2ApiItem): PartnerInfo {
  const account = api.saleContext.vtexAccount;
  const name = account.name && account.name.trim().length > 0 ? account.name : account.id;
  return {
    name,
    id: account.id,
    isPremiumSeller: account.type === 'Franchise',
  };
}

function mapSeller(api: OrderV2ApiItem): SellerInfo | null {
  const salesperson = api.saleContext.salesperson;
  if (!salesperson) {
    return null;
  }
  return {
    id: salesperson.id ?? '',
    name: salesperson.name ?? '',
    priceTable: salesperson.priceTable ?? '',
    cashierNumber: salesperson.cashierNumber ?? '',
    cashierStoreId: salesperson.cashierStoreId ?? '',
  };
}
